import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSubTour } from './SubTourContext';
import { Button } from '../ui/button';
import { 
  ChevronLeft, 
  ChevronRight, 
  X, 
  CheckCircle2,
  Plus,
  Download,
  Pause,
  Play,
  MousePointerClick
} from 'lucide-react';

const SubTourOverlay = () => {
  const {
    activeSubTour,
    currentStep,
    currentStepIndex,
    totalSteps,
    currentTour,
    selectedChoice,
    isPaused,
    selectChoice,
    nextStep,
    prevStep,
    resumeTour,
    exitTour,
    completeTour,
    startNextTour
  } = useSubTour();

  const [targetRect, setTargetRect] = useState(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [nextTourId, setNextTourId] = useState(null);

  // Handle click on target element when clickTarget is true
  const handleTargetClick = useCallback(() => {
    if (currentStep?.clickTarget) {
      nextStep();
    }
  }, [currentStep, nextStep]);

  // Find and track target element, add click listener if needed
  useEffect(() => {
    if (!activeSubTour || !currentStep?.target) {
      setTargetRect(null);
      return;
    }

    let targetElement = null;

    const findTarget = () => {
      const target = document.querySelector(currentStep.target);
      if (target) {
        targetElement = target;
        const rect = target.getBoundingClientRect();
        const padding = 8;
        setTargetRect({
          top: rect.top - padding,
          left: rect.left - padding,
          width: rect.width + padding * 2,
          height: rect.height + padding * 2
        });

        // Scroll into view if needed — but NOT for form steps, where the user
        // must be free to scroll through a long form without the overlay
        // constantly fighting them back to the target's center.
        const isForm = currentStep.type === 'form' && currentStep.pauseForInput;
        if (!isForm && (rect.top < 0 || rect.bottom > window.innerHeight)) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        
        // Add click listener if this step requires clicking the target
        if (currentStep.clickTarget) {
          target.addEventListener('click', handleTargetClick);
        }
      } else {
        setTargetRect(null);
      }
    };

    findTarget();
    const interval = setInterval(findTarget, 500);

    window.addEventListener('resize', findTarget);
    window.addEventListener('scroll', findTarget, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', findTarget);
      window.removeEventListener('scroll', findTarget, true);
      // Remove click listener from target element
      if (targetElement && currentStep?.clickTarget) {
        targetElement.removeEventListener('click', handleTargetClick);
      }
    };
  }, [activeSubTour, currentStep, handleTargetClick]);

  if (!activeSubTour || !currentStep) return null;

  const handleExit = () => {
    setShowExitConfirm(true);
  };

  const confirmExit = () => {
    setShowExitConfirm(false);
    exitTour();
  };

  const handleComplete = () => {
    const nextId = completeTour();
    if (nextId) {
      setNextTourId(nextId);
    }
  };

  const handleStartNextTour = () => {
    startNextTour(nextTourId);
    setNextTourId(null);
  };

  const handleSkipNextTour = () => {
    setNextTourId(null);
  };

  const isIntroStep = currentStep.type === 'info' && currentStep.choices;
  const isCompleteStep = currentStep.type === 'complete';
  const isFormStep = currentStep.type === 'form' && currentStep.pauseForInput;
  const isClickTargetStep = currentStep.clickTarget === true;
  const isInfoOnlyStep = currentStep.type === 'info' && !currentStep.choices;
  // Don't show blocking overlay for intro steps, complete steps, info-only steps, or form steps
  const showOverlay = !isCompleteStep && !isInfoOnlyStep && !isIntroStep && !isFormStep;

  // Icon mapping for choices
  const getChoiceIcon = (iconName) => {
    switch (iconName) {
      case 'Plus': return <Plus className="h-4 w-4" />;
      case 'Download': return <Download className="h-4 w-4" />;
      default: return null;
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9998] pointer-events-none" data-tour-overlay>
      {/* Dark overlay using 4 rectangles around spotlight - allows clicks through spotlight */}
      {showOverlay && targetRect && (
        <>
          {/* Top rectangle */}
          <div 
            className="fixed left-0 right-0 top-0 bg-black/75 pointer-events-auto"
            style={{ height: targetRect.top }}
          />
          {/* Bottom rectangle */}
          <div 
            className="fixed left-0 right-0 bottom-0 bg-black/75 pointer-events-auto"
            style={{ top: targetRect.top + targetRect.height }}
          />
          {/* Left rectangle */}
          <div 
            className="fixed left-0 bg-black/75 pointer-events-auto"
            style={{ 
              top: targetRect.top,
              height: targetRect.height,
              width: targetRect.left
            }}
          />
          {/* Right rectangle */}
          <div 
            className="fixed right-0 bg-black/75 pointer-events-auto"
            style={{ 
              top: targetRect.top,
              height: targetRect.height,
              left: targetRect.left + targetRect.width
            }}
          />
        </>
      )}

      {/* Full overlay when no target (for intro/complete steps) */}
      {showOverlay && !targetRect && (
        <div className="fixed inset-0 bg-black/75 pointer-events-auto" />
      )}

      {/* Semi-transparent backdrop for intro steps - doesn't block dialog */}
      {isIntroStep && (
        <div className="fixed inset-0 bg-black/60 pointer-events-none" />
      )}

      {/* Semi-transparent backdrop for form steps - allows form interaction */}
      {isFormStep && (
        <div className="fixed inset-0 bg-black/10 pointer-events-none" />
      )}

      {/* Spotlight border/glow effect */}
      {showOverlay && targetRect && (
        <div
          className="fixed border-2 border-[#ffd108] rounded-lg pointer-events-none"
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
            boxShadow: '0 0 0 4px rgba(255, 209, 8, 0.3), 0 0 20px rgba(255, 209, 8, 0.4)'
          }}
        />
      )}

      {/* Tooltip - positioned based on step type */}
      <div 
        className="fixed pointer-events-auto"
        style={
          isFormStep 
            ? {
                // For form steps, position at bottom-left to not cover the drawer
                bottom: 24,
                left: 24
              }
            : isIntroStep || isCompleteStep
            ? {
                // For intro/complete steps, center the tooltip
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)'
              }
            : {
                // For action steps, position near the target
                ...(currentStep.placement === 'right' && targetRect ? {
                  top: Math.max(16, Math.min(targetRect.top, window.innerHeight - 300)),
                  left: targetRect.left + targetRect.width + 16,
                } : {
                  top: targetRect ? Math.min(targetRect.top + targetRect.height + 16, window.innerHeight - 300) : '50%',
                  left: targetRect ? Math.max(16, Math.min(targetRect.left, window.innerWidth - 400)) : '50%',
                }),
                transform: targetRect ? 'none' : 'translate(-50%, -50%)'
              }
        }
      >
        <div className="w-[380px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-zinc-800 px-4 py-3 flex items-center justify-between border-b border-zinc-700">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#ffd108]">
                {currentTour?.title}
              </span>
              {!isIntroStep && !isCompleteStep && (
                <span className="text-sm text-zinc-500">
                  • Step {currentStepIndex + 1} of {totalSteps}
                </span>
              )}
            </div>
            <button
              onClick={handleExit}
              className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5">
            {isCompleteStep && (
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                </div>
                <h3 className="font-semibold text-white text-lg">
                  {currentStep.title}
                </h3>
              </div>
            )}
            
            {!isCompleteStep && (
              <h3 className="font-semibold text-white text-lg mb-2">
                {currentStep.title}
              </h3>
            )}
            
            <p className="text-zinc-400 text-sm leading-relaxed">
              {currentStep.content}
            </p>

            {/* Choice buttons for intro steps */}
            {isIntroStep && currentStep.choices && (
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                {currentStep.choices.map((choice) => (
                  <Button
                    key={choice.id}
                    onClick={() => selectChoice(choice.id)}
                    variant={choice.id === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    className={choice.id === 'manual' 
                      ? 'bg-[#ffd108] text-black hover:bg-[#ffd108]/90' 
                      : 'border-zinc-600 text-white hover:bg-zinc-800'
                    }
                  >
                    {getChoiceIcon(choice.icon)}
                    <span className="ml-2">{choice.label}</span>
                  </Button>
                ))}
              </div>
            )}

            {/* Pause indicator for form steps */}
            {isFormStep && isPaused && (
              <div className="mt-4 p-3 bg-zinc-800 rounded-lg flex items-center gap-3">
                <Pause className="h-4 w-4 text-[#ffd108]" />
                <span className="text-sm text-zinc-300">
                  Complete the form, then click Continue
                </span>
              </div>
            )}
          </div>

          {/* Progress bar */}
          {!isIntroStep && (
            <div className="px-5 pb-2">
              <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#ffd108] transition-all duration-300"
                  style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="px-5 pb-5 flex items-center justify-between">
            {!isIntroStep && !isCompleteStep && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prevStep}
                  disabled={currentStepIndex === 0}
                  className="text-zinc-400 hover:text-white hover:bg-zinc-800 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>

                {isClickTargetStep ? (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500 flex items-center gap-1">
                      <MousePointerClick className="h-3 w-3" />
                      Click highlighted element
                    </span>
                    <Button
                      size="sm"
                      onClick={nextStep}
                      className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                ) : isFormStep ? (
                  <Button
                    size="sm"
                    onClick={resumeTour}
                    className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium"
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Continue
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={nextStep}
                    className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </>
            )}

            {isIntroStep && !currentStep.choices && (
              <Button
                size="sm"
                onClick={nextStep}
                className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium ml-auto"
              >
                Let's Go
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {isCompleteStep && (
              <Button
                size="sm"
                onClick={handleComplete}
                className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 font-medium ml-auto"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Complete
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      {showExitConfirm && (
        <div className="fixed inset-0 z-[10001] pointer-events-auto">
          <div 
            className="fixed inset-0 bg-black/60"
            onClick={() => setShowExitConfirm(false)}
          />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 shadow-2xl">
              <h2 className="text-lg font-semibold text-white mb-2">Exit this guide?</h2>
              <p className="text-zinc-400 text-sm mb-6">
                Your progress won't be saved. You can restart this guide anytime from the Training section.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowExitConfirm(false)}
                  className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700"
                >
                  Continue Guide
                </Button>
                <Button
                  onClick={confirmExit}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Exit Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Tour Suggestion Dialog */}
      {nextTourId && (
        <div className="fixed inset-0 z-[10001] pointer-events-auto">
          <div className="fixed inset-0 bg-black/60" />
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5 text-[#ffd108]" />
                </div>
                <h2 className="text-lg font-semibold text-white">Great progress!</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-6">
                Ready to continue with the next guide?
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={handleSkipNextTour}
                  className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700"
                >
                  Maybe Later
                </Button>
                <Button
                  onClick={handleStartNextTour}
                  className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90"
                >
                  Continue
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default SubTourOverlay;
